import { useAllWorkerEvents } from "@/hooks/useWorkerEvent";
import { useEngineWorker } from "@/hooks/useEngineWorker";
import { useEngineStore } from "@/stores/engineStore";
import { useEditorStore } from "@/stores/editorStore";
import { useDebugStore } from "@/stores/debugStore";
import { useAIStore } from "@/stores/aiStore";
import { getWorkerManager } from "@/lib/WorkerManager";
import { AppLayout } from "@/components/layout/AppLayout";

/**
 * Root application component.
 * Bootstraps engine workers, wires WorkerManager events to Zustand stores,
 * and renders the IDE shell.
 */
export function App() {
  const handleEngineEvent = useEngineStore((s) => s.handleEngineEvent);
  const setSelection = useEditorStore((s) => s.setSelection);
  const setViewport = useEditorStore((s) => s.setViewport);
  const setUndoRedoState = useEditorStore((s) => s.setUndoRedoState);
  const setDirty = useEditorStore((s) => s.setDirty);
  const setConnectionHint = useEditorStore((s) => s.setConnectionHint);
  const activeTabId = useEditorStore((s) => s.activeTabId);

  // Bootstrap engine workers on app start so they are ready for deploy/run
  useEngineWorker({ role: "bpmn-engine", autoStart: true });
  useEngineWorker({ role: "node-engine", autoStart: true });

  // Wire all worker events to the appropriate stores
  useAllWorkerEvents((workerId, eventType, payload) => {
    // Engine events -> engineStore
    const engineEvents = [
      "ready",
      "error",
      "instanceCreated",
      "tokenMoved",
      "jobCreated",
      "jobActivated",
      "jobCompleted",
      "instanceCompleted",
      "incidentRaised",
    ];
    if (engineEvents.includes(eventType)) {
      // For "ready" events, use WorkerManager's registered role (authoritative)
      // instead of the payload role (C++ activation name like "bpmn"/"node")
      if (eventType === "ready") {
        const registeredRole = getWorkerManager().getWorkerRole(workerId);
        handleEngineEvent(workerId, eventType, {
          ...payload,
          role: registeredRole ?? payload.role,
        });
      } else {
        handleEngineEvent(workerId, eventType, payload);
      }
    }

    // Editor events -> editorStore
    if (eventType === "selectionChanged" && activeTabId) {
      setSelection(activeTabId, {
        selectedIds: (payload.selectedIds as string[]) ?? [],
        elementType: payload.elementType as string | undefined,
        elementProperties: payload.elementProperties
          ? JSON.parse(payload.elementProperties as string)
          : undefined,
      });
    }

    if (eventType === "viewportChanged" && activeTabId) {
      setViewport(activeTabId, {
        zoom: Number(payload.zoom),
        centerX: Number(payload.centerX),
        centerY: Number(payload.centerY),
      });
    }

    if (eventType === "documentModified" && activeTabId) {
      setUndoRedoState(activeTabId, Boolean(payload.canUndo), Boolean(payload.canRedo));
      setDirty(activeTabId, Boolean(payload.dirty));
    }

    // Connection drag lifecycle events -> editorStore connectionHint (Phase 24, Plan 03; Phase 30 editor: prefix)
    // Hint behavior per CONTEXT.md: completed=clear, empty_canvas=mode hint, invalid_target=warning, cancelled=clear
    if (eventType === "editor:connectDragStarted") {
      setConnectionHint("Click source element, then drag to target");
    }
    if (eventType === "editor:connectDragEnded") {
      const status = payload.status as string;
      if (status === "completed") {
        // Clear hint -- the created connection is visual confirmation enough
        setConnectionHint("");
      } else if (status === "empty_canvas") {
        // Return to connection mode hint -- user is still in mode
        setConnectionHint("Click source element, then drag to target");
      } else if (status === "invalid_target") {
        // Warning hint -- explains why drop failed
        setConnectionHint("Cannot connect to this element");
      } else if (status === "cancelled") {
        // Clear hint -- user exits connection mode
        setConnectionHint("");
      }
    }
    if (eventType === "editor:connectHoverChanged") {
      const valid = payload.valid as boolean;
      if (valid) {
        // Dynamic hint during active drag over valid target
        setConnectionHint("Release to connect");
      } else {
        // Restore mode hint -- still dragging, just not over a valid target
        setConnectionHint("Click source element, then drag to target");
      }
    }

    // Property change failed — selection already reverted by worker
    if (eventType === "propertyChangeFailed" && payload.key === "id") {
      if (payload.reason === "id_exists") {
        window.alert("This ID is already in use. Please choose a unique ID.");
      } else if (payload.reason === "id_empty") {
        window.alert("ID cannot be empty.");
      }
    }

    // AI auto-diagnosis: incidentRaised -> aiStore.triggerDiagnosis (Phase 21, Plan 03)
    if (eventType === "incidentRaised") {
      const aiState = useAIStore.getState();
      if (aiState.apiKey && aiState.autoDiagnoseEnabled) {
        const editorState = useEditorStore.getState();
        const tab = editorState.getActiveTab();
        if (tab) {
          aiState.triggerDiagnosis({
            tabId: tab.id,
            errorType: String(payload.error_type ?? "incident"),
            errorMessage: String(payload.error_message ?? payload.message ?? "Unknown error"),
            elementId: String(payload.element_id ?? ""),
            diagramContent: tab.content ?? "",
            diagramType: tab.editorType as "bpmn" | "nodeflow",
          });
        }
      }
    }

    // AI auto-diagnosis: validationResults -> aiStore.triggerDiagnosis (Phase 21, Plan 03)
    // payload.results is already a parsed JS array from postMessage -- no JSON.parse needed.
    if (eventType === "validationResults") {
      const aiState = useAIStore.getState();
      if (aiState.apiKey && aiState.autoDiagnoseEnabled) {
        const results = (payload.results ?? []) as {
          severity: string;
          message: string;
          elementId?: string;
        }[];
        const errors = results.filter((r) => r.severity === "error");
        if (errors.length > 0) {
          const editorState = useEditorStore.getState();
          const tab = activeTabId
            ? editorState.tabs.find((t) => t.id === activeTabId)
            : undefined;
          if (tab) {
            aiState.triggerDiagnosis({
              tabId: tab.id,
              errorType: "validation",
              errorMessage: errors.map((e) => e.message).join("; "),
              elementId: errors[0]?.elementId ?? "",
              diagramContent: tab.content ?? "",
              diagramType: tab.editorType as "bpmn" | "nodeflow",
            });
          }
        }
      }
    }

    // Debug events -> debugStore (Phase 20, Plan 03)
    const debugEvents = ["breakpointHit", "stepCompleted", "elementTiming", "executionPaused"];
    if (debugEvents.includes(eventType)) {
      const payloadStrings = payload as Record<string, string>;
      useDebugStore.getState().handleDebugEvent(eventType, payloadStrings);

      // Visual overlay updates for step/breakpoint events
      if (eventType === "stepCompleted" || eventType === "breakpointHit") {
        const manager = getWorkerManager();
        const editorWorkerId = manager.getWorkerIdByRole("bpmn-editor");
        if (editorWorkerId) {
          // Highlight current element token position
          manager
            .sendCommand(
              editorWorkerId,
              {
                type: "debugOverlay" as const,
                direction: "cmd" as const,
                subCommand: "showToken",
                payload: {
                  bpmnElementId: payloadStrings.element_id,
                },
              } as never,
              5000,
            )
            .catch(() => {
              // Best-effort: editor may not support this overlay yet
            });

          // Animate token from previous to current element (stepCompleted only)
          if (eventType === "stepCompleted" && payloadStrings.previous_element_id) {
            manager
              .sendCommand(
                editorWorkerId,
                {
                  type: "debugOverlay" as const,
                  direction: "cmd" as const,
                  subCommand: "animateToken",
                  payload: {
                    fromBpmnElementId: payloadStrings.previous_element_id,
                    toBpmnElementId: payloadStrings.element_id,
                    duration: 500 / useDebugStore.getState().animationSpeed,
                  },
                } as never,
                5000,
              )
              .catch(() => {
                // Best-effort
              });
          }
        }

        // Log breakpoint hit to console
        if (eventType === "breakpointHit") {
          console.info(`[Debug] Breakpoint hit at ${payloadStrings.element_id}`);
        }
      }

      if (eventType === "executionPaused") {
        console.info(`[Debug] Execution paused at ${(payload as Record<string, string>).element_id ?? "unknown"}`);
      }
    }
  });

  return <AppLayout />;
}
